// Import real services directly for consistency
import {
  RealBillingRecordsService,
  RealShopWorxService,
  RealBillingCalculationService,
} from '../../services/realApi';
import appConfig from '../../config/appConfig';

const initialState = {
  billingRecords: [],
  currentBill: null,
  consumptionData: null,
  loading: false,
  error: null,
};

const mutations = {
  SET_BILLING_RECORDS(state, records) {
    state.billingRecords = records;
  },
  SET_CURRENT_BILL(state, bill) {
    state.currentBill = bill;
  },
  SET_CONSUMPTION_DATA(state, data) {
    state.consumptionData = data;
  },
  ADD_BILLING_RECORD(state, record) {
    state.billingRecords.push(record);
  },
  UPDATE_BILLING_RECORD(state, updatedRecord) {
    const index = state.billingRecords.findIndex((record) => record.id === updatedRecord.id
      || record._id === updatedRecord._id
      || record.id === updatedRecord._id
      || record._id === updatedRecord.id);
    if (index !== -1) {
      state.billingRecords.splice(index, 1, updatedRecord);
    } else {
      state.billingRecords.unshift(updatedRecord);
    }
  },
  REMOVE_BILLING_RECORD(state, recordId) {
    const index = state.billingRecords.findIndex((record) => (
      record.id === recordId || record._id === recordId
    ));
    if (index !== -1) {
      state.billingRecords.splice(index, 1);
    }
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
};

const actions = {
  async fetchBillingRecords({ commit }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      // Use RealBillingRecordsService for consistency
      const records = await RealBillingRecordsService.getAllRecords();

      // Ensure we always have an array
      const recordsArray = Array.isArray(records) ? records : [];
      commit('SET_BILLING_RECORDS', recordsArray);
      return recordsArray;
    } catch (error) {
      console.error('❌ Error fetching billing records:', error);
      commit('SET_ERROR', error.message);

      // Return empty array on error to prevent overlap detection issues
      commit('SET_BILLING_RECORDS', []);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchConsumptionData({ commit }, { startDate, endDate }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      // Use RealShopWorxService for consistency
      const data = await RealShopWorxService.getConsumptionData(startDate, endDate);
      commit('SET_CONSUMPTION_DATA', data);
      return data;
    } catch (error) {
      console.error('❌ Error fetching consumption data:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async calculateBill({ commit }, {
    consumptionData,
    kvaDemand,
    billingDate,
    billingMode,
    tariffMode,
    tariffData,
    activeCategories,
    rates,
  }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const billResult = await RealBillingCalculationService.calculateBill(
        consumptionData,
        kvaDemand,
        billingDate,
        {
          billingMode,
          tariffMode,
          tariffData,
          activeCategories,
          rates,
        },
      );

      const calculatedBill = {
        billingPeriod: consumptionData.billingPeriod,
        consumptionData,
        kvaDemand,
        calculatedCharges: billResult.calculatedCharges,
        subtotal: billResult.subtotal || billResult.calculatedCharges.totalCalculated || 0,
        actualBillAmount: 0, // To be entered manually
        variance: 0,
        ratesUsed: billResult.ratesUsed || [],
        calculationMode: billResult.calculationMode,
        unitCostForBilling: (
          billResult.calculatedCharges?.BILLING_UNIT_COST || 0
        ),
        createdBy: appConfig.getParentUserName(),
        createdAt: new Date(),
      };

      commit('SET_CURRENT_BILL', calculatedBill);
      return calculatedBill;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async saveBillingRecord({ commit }, recordData) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      // Use RealBillingRecordsService for consistency
      const newRecord = await RealBillingRecordsService.createRecord(recordData);
      commit('ADD_BILLING_RECORD', newRecord);
      return newRecord;
    } catch (error) {
      console.error('❌ Error saving billing record:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteBillingRecord({ commit }, recordId) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      // Use RealBillingRecordsService for soft delete operations (sets is_active: false)
      await RealBillingRecordsService.deleteRecord(recordId);
      commit('REMOVE_BILLING_RECORD', recordId);
      return true;
    } catch (error) {
      console.error('❌ Error soft deleting billing record:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateBillingRecord({ commit }, { id, recordData }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const updatedRecord = await RealBillingRecordsService.updateRecord(id, recordData);
      commit('UPDATE_BILLING_RECORD', updatedRecord);
      commit('SET_CURRENT_BILL', updatedRecord);
      return updatedRecord;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchBillingRecordForEdit({ commit }, id) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const record = await RealBillingRecordsService.getRecordById(id);
      if (record) {
        commit('SET_CURRENT_BILL', record);
      }
      return record;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  clearError({ commit }) {
    commit('SET_ERROR', null);
  },
};

const getters = {
  allBillingRecords: (state) => state.billingRecords,
  currentBill: (state) => state.currentBill,
  consumptionData: (state) => state.consumptionData,
  loading: (state) => state.loading,
  error: (state) => state.error,
  getBillingRecordById: (state) => (id) => state.billingRecords.find((record) => (
    record.id === id || record._id === id
  )),
  isEditMode: (state) => state.currentBill && (state.currentBill.id || state.currentBill._id),
  getBillingRecordForEdit: (state) => (id) => state.billingRecords.find((record) => (
    record.id === id || record._id === id
  )),
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
};
